import { useContext, useEffect, useMemo } from 'react'
import { useColorScheme } from '@mui/material/styles'

import { Typography, FormControl, FormControlLabel, FormLabel, RadioGroup, Radio } from '@mui/material'

import LightModeOutlinesIcon from '@mui/icons-material/LightModeOutlined'
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined'
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'

import { AppContext } from "/providers/app-context.js"
import _ from 'lodash'

const Settings = () => {

  const { mode, setMode, systemMode } = useColorScheme()
  const { apiSource, setApiSource, user, saveUser } = useContext(AppContext)

  const themeMode = useMemo(() => {
    if (mode === "system") return systemMode
    return mode
  }, [mode, systemMode])

  const setColourScheme = scheme => {
    setMode(scheme)
    if (user) setUserPreference("colour_scheme", scheme)
  }

  const setUserPreference = (key, value) => {
    let u = _.cloneDeep(user)
    u.preferences[key] = value
    saveUser(u)
  }

  useEffect(() => {
    document.body.setAttribute('data-theme', themeMode)
    if (user) setMode(user.preferences?.colour_scheme)
  }, [user, mode, systemMode])

  if (user) return (
    <div className='p-5'>
      <Typography variant='h4'>Settings</Typography>

      <FormControl>
        <FormLabel id="theme-toggle" className='mt-5'>Theme</FormLabel>
        <RadioGroup
          aria-labelledby="theme-toggle"
          row
          value={user.preferences?.colour_scheme}
          onChange={e => setColourScheme(e.target.value)}
        >
          <FormControlLabel value="system" control={<Radio />} label={<><SettingsSuggestOutlinedIcon /> System</>} />
          <FormControlLabel value="light" control={<Radio />} label={<><LightModeOutlinesIcon /> Light</>} />
          <FormControlLabel value="dark" control={<Radio />} label={<><DarkModeOutlinedIcon /> Dark</>} />
        </RadioGroup>

        <FormLabel id="editable-items" className='mt-5'>Highlight Editable Items</FormLabel>
        <RadioGroup
          aria-labelledby="editable-items"
          row
          value={user.preferences?.highlight_editable_items}
          onChange={e => setUserPreference("highlight_editable_items", e.target.value === 'true')}
        >
          <FormControlLabel value={false} control={<Radio />} label={<><VisibilityOffOutlinedIcon /> Hide</>} />
          <FormControlLabel value={true} control={<Radio />} label={<><VisibilityOutlinedIcon /> Show</>} />
        </RadioGroup>

        You can toggle this setting in the editor with key combination Ctrl+Alt+E / Ctrl+Option+E

        <FormLabel id="editable-items" className='mt-5'>Show auto-generated content</FormLabel>
        <RadioGroup
          aria-labelledby="editable-items"
          row
          value={user.preferences?.show_standard_items}
          onChange={e => setUserPreference("show_standard_items", e.target.value === 'true')}
        >
          <FormControlLabel value={false} control={<Radio />} label={<><VisibilityOffOutlinedIcon /> Hide</>} />
          <FormControlLabel value={true} control={<Radio />} label={<><VisibilityOutlinedIcon /> Show</>} />
        </RadioGroup>

        <FormLabel id="data-format" className='mt-5'>Data download format</FormLabel>
        <RadioGroup
          aria-labelledby="data-format"
          row
          value={user.preferences?.data_format}
          onChange={e => setUserPreference("data_format", e.target.value)}
        >
          <FormControlLabel value="json" control={<Radio />} label="{...} JSON" />
          <FormControlLabel value="csv" control={<Radio />} label="[,,,] CSV" />
          <FormControlLabel value="xls" control={<Radio />} label="XLSX - Microsoft Excel" />
        </RadioGroup>

        {["localhost","127.0.0.1","local.smartix.uk"].includes(window.location.hostname) &&
          <>
            <FormLabel id="api-source" className='mt-5'>API Source</FormLabel>
            <RadioGroup
              aria-labelledby="api-source"
              row
              value={apiSource}
              onChange={e =>
                setApiSource(e.target.value)
              }
            >
              <FormControlLabel value="remote" control={<Radio />} label={<> Remote</>} />
              <FormControlLabel value="local" control={<Radio />} label={<> Local</>} />
            </RadioGroup>
          </>
        }

      </FormControl>
    </div>
  )

  return (
    <div className='p-5'>
      <Typography variant='h4'>Settings</Typography>

      <FormControl>
        <FormLabel id="theme-toggle" className='mt-5'>Theme [{mode}]</FormLabel>
        <RadioGroup
          aria-labelledby="theme-toggle"
          row
          value={mode}
          onChange={e => setColourScheme(e.target.value)}
        >
          <FormControlLabel value="system" control={<Radio />} label={<><SettingsSuggestOutlinedIcon /> System</>} />
          <FormControlLabel value="light" control={<Radio />} label={<><LightModeOutlinesIcon /> Light</>} />
          <FormControlLabel value="dark" control={<Radio />} label={<><DarkModeOutlinedIcon /> Dark</>} />
        </RadioGroup>

        {["localhost","127.0.0.1","local.smartix.uk"].includes(window.location.hostname) &&
          <>
            <FormLabel id="api-source" className='mt-5'>API Source</FormLabel>
            <RadioGroup
              aria-labelledby="api-source"
              row
              value={apiSource}
              onChange={e =>
                setApiSource(e.target.value)
              }
            >
              <FormControlLabel value="remote" control={<Radio />} label={<> Remote</>} />
              <FormControlLabel value="local" control={<Radio />} label={<> Local</>} />
            </RadioGroup>
          </>
        }
      </FormControl>

    </div>
  )
}

export default Settings 